// `react-router`, not `react-router-dom`. v7 merged them and this package
// depends on the former only, so the `-dom` specifier does not resolve at all.
import { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, type To } from 'react-router';
import {
  RoleSchema,
  canHoldPassword,
  canReadRsvpRollup,
  canReadSchedule,
  canViewAdminDashboard,
  canViewAllergyCard,
  canWriteThread,
  type Role,
} from '@good-season/schema';

import { api } from '../api/client.js';
import { useHeaderFit } from './useHeaderFit.js';
import { BrandMark } from './BrandMark.js';
import { TeamSwitcher } from './TeamSwitcher.js';
import { useTeams, type ClubHome, type TeamMembership } from '../team/useTeams.js';

/**
 * The frame every signed-in screen sits inside.
 *
 * There was no shell at all until now, which is why the app read as a single
 * loose page: no header carrying which club and season you are looking at, no
 * way to move between screens, and no footer. The owner's words on seeing it
 * for the first time were that it looked like crap, and the diagnosis was
 * exactly this.
 *
 * ## Responsive, not two components
 *
 * Top navigation on wide screens, bottom tabs on a phone, one component that
 * switches at 640px. That is the SAME breakpoint `CLAUDE.md` already uses for
 * Dialogs becoming Sheets, so the app has one place where it stops being a
 * phone rather than two that can disagree.
 *
 * Bottom tabs on a phone because BUILD_PLAN's premise is a parent opening this
 * for thirty seconds while standing up, and a thumb reaches the bottom of a
 * phone and not the top of it. Top nav on a laptop because a coach filling a
 * roster is sitting down, and a tab bar pinned to the bottom of a 1400px window
 * is a phone affordation wearing the wrong size.
 *
 * ## Sections are what this role can open, and nothing else (ADR-0094)
 *
 * A destination is listed only when its screen exists AND the signed-in role
 * can open it, using the predicates in `@good-season/schema` and no logic of
 * its own. The dimmed "Scorebook soon" entry is gone: a parent was being shown
 * a screen they could not use. The scorebook is reached from the schedule.
 *
 * Every item carries an icon AND a visible label. The icon is `aria-hidden`
 * because the label beside it is the accessible name; an icon is never the
 * only carrier of meaning.
 */

type SectionId =
  'home' | 'team' | 'schedule' | 'announcements' | 'availability' | 'allergy' | 'admin';

/** A destination that exists. */
interface Destination {
  readonly id: SectionId;
  readonly to: string;
  readonly label: string;
}

/*
 * Ordered as a season runs: where you are, who is on the team, when they play,
 * who can come, then the sections only some roles hold. Members and families
 * live under Admin, one team at a time, rather than as a seventh tab.
 */
const HOME: Destination = { id: 'home', to: '/', label: 'Home' };
const TEAM: Destination = { id: 'team', to: '/team', label: 'Team' };
const SCHEDULE: Destination = { id: 'schedule', to: '/schedule', label: 'Schedule' };
/*
 * Fourth, after Schedule, so the parent order is a prefix of the coach order.
 * A fourth parent destination amends BUILD_PLAN D19 (ADR-0122).
 */
const ANNOUNCEMENTS: Destination = {
  id: 'announcements',
  to: '/announcements',
  label: 'Announcements',
};
const AVAILABILITY: Destination = { id: 'availability', to: '/rsvp', label: 'Availability' };
const ALLERGY: Destination = { id: 'allergy', to: '/allergy-card', label: 'Allergy card' };
const ADMIN: Destination = { id: 'admin', to: '/admin', label: 'Admin' };

/**
 * The roles that decide every item except Admin, and whether ALL of them must
 * allow an item.
 *
 * The role the screen was given, when it is a real role: that is the
 * membership the server resolved for this screen, so it alone decides.
 *
 * Otherwise the screen did not say (Landing, the allergy card, the admin
 * screen, any screen still loading), and the server will resolve ONE
 * membership that this component cannot see. So every candidate role must
 * allow an item before it is shown: the roles held on the team being shown
 * when `?team=` names one, else every role held anywhere. A guardian of one
 * team who coaches another sees only what a guardian can open until a screen
 * names the coaching role. Under-showing costs a tap; over-showing sends a
 * person to a screen that refuses them (ADR-0094 point 3).
 *
 * `RoleSchema.safeParse` rather than a cast, because a role arrives as a bare
 * string over the wire and an unknown one from a newer server must read as
 * "no role". Nothing known yet yields no roles, so only the ungated items show.
 */
function rolesFor(
  role: string | undefined,
  teamId: string | undefined,
  memberships: readonly TeamMembership[],
): { readonly roles: readonly Role[]; readonly allMustAllow: boolean } {
  const own = RoleSchema.safeParse(role);
  if (own.success) return { roles: [own.data], allMustAllow: false };
  const parsed = (list: readonly TeamMembership[]): Role[] =>
    list.flatMap((membership) => {
      const result = RoleSchema.safeParse(membership.role);
      return result.success ? [result.data] : [];
    });
  const here = teamId === undefined ? [] : memberships.filter((m) => m.teamId === teamId);
  return { roles: here.length > 0 ? parsed(here) : parsed(memberships), allMustAllow: true };
}

/**
 * The sections this person can open.
 *
 * Admin reads EVERY membership, not merely the one currently selected: a Head
 * Coach of one team and a Guardian of another must still see this entry
 * while looking at the guardian team, the same way the dashboard itself
 * aggregates across every team rather than only the active one. The screen's
 * own role counts too, so a failed memberships read does not hide Admin from
 * a head coach on a screen that knows its role.
 *
 * Known gap: this reads every held membership, not the per-team CURRENT one,
 * so a person whose role on a team changed between seasons can still see
 * this entry on their prior season's standing; the server's
 * `currentMembershipsFor` is the actual authority and answers with the
 * designed forbidden state rather than data. See ADR-0074.
 */
export function visibleDestinations(
  role: string | undefined,
  teamId: string | undefined,
  memberships: readonly TeamMembership[],
  /**
   * An Owner whose only admin teams are archived holds no admin membership,
   * and still needs Admin to restore one (ADR-0095).
   */
  canRestoreTeams = false,
  /**
   * A live club grant. With no team, the shell shows the club instead of a
   * team nav that would only refuse. With a team as well, Admin stays
   * available beside that team.
   */
  hasClubGrant = false,
): readonly Destination[] {
  const { roles, allMustAllow } = rolesFor(role, teamId, memberships);
  if (hasClubGrant && memberships.length === 0 && roles.length === 0 && !canRestoreTeams) {
    return [ADMIN];
  }
  const allows = (predicate: (some: readonly Role[]) => boolean): boolean =>
    roles.length > 0 && (allMustAllow ? roles.every((one) => predicate([one])) : predicate(roles));
  const own = RoleSchema.safeParse(role);
  const isAdmin =
    (own.success && canViewAdminDashboard(own.data)) ||
    memberships.some((membership) => {
      const parsed = RoleSchema.safeParse(membership.role);
      return parsed.success && canViewAdminDashboard(parsed.data);
    });
  /*
   * Home is not offered to a scorekeeper. `/` sends that role straight to the
   * team screen (see `Landing`), so a Home item would be a link that bounces
   * to the item beside it. Only when EVERY known role is scorekeeper: nothing
   * known yet (Landing before it loads) and a mixed set both keep Home.
   */
  const scorekeeperOnly = roles.length > 0 && roles.every((held) => held === 'scorekeeper');
  return [
    ...(scorekeeperOnly ? [] : [HOME]),
    TEAM,
    ...(allows(canReadSchedule) ? [SCHEDULE] : []),
    ...(allows(canWriteThread) ? [ANNOUNCEMENTS] : []),
    ...(allows(canReadRsvpRollup) ? [AVAILABILITY] : []),
    ...(allows((some) => some.some(canViewAllergyCard)) ? [ALLERGY] : []),
    ...(isAdmin || canRestoreTeams || hasClubGrant ? [ADMIN] : []),
  ];
}

/*
 * Inline stroke icons on a 24px grid, drawn for this app. `currentColor` so the
 * nav's text colour carries them, and `aria-hidden` because the label next to
 * each one is the name.
 */
const ICON_PATHS: Readonly<Record<SectionId, React.ReactNode>> = {
  home: <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,
  team: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16 14.5c2.6 0 4.6 1.5 5.4 4.5" />
    </>
  ),
  schedule: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  announcements: (
    <>
      <path d="M3 10v4a1 1 0 0 0 1 1h3l8 4V5L7 9H4a1 1 0 0 0-1 1z" />
      <path d="M18.5 8.5a5 5 0 0 1 0 7" />
    </>
  ),
  availability: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M8 14l3 3 5-6" />
    </>
  ),
  allergy: (
    <>
      <path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z" />
      <path d="M12 8v5M12 16v.5" />
    </>
  ),
  admin: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
};

function NavIcon({ id }: { readonly id: SectionId }) {
  return (
    <svg
      className="shell__navIcon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[id]}
    </svg>
  );
}

/** Up to two initials for the team badge: "Northgate Falcons" is "NF". */
function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => Array.from(word)[0]?.toUpperCase() ?? '');
  return letters.join('');
}

export interface AppShellProps {
  /** The club, shown small above the team. Absent while it is still loading. */
  readonly organizationName?: string | undefined;
  /** The team, which is the thing somebody actually recognises. */
  readonly teamName?: string | undefined;
  /**
   * The id behind `teamName`, so the switcher can mark it in the list.
   *
   * Optional, and separately from `teamName`: `Landing` renders this shell
   * before it knows a team at all, which is a deliberate choice documented on
   * that screen, not an oversight here. Without it the switcher still opens
   * and still switches; it just cannot say which row is the one already
   * showing.
   */
  readonly teamId?: string | undefined;
  /** Season name, and whether it is the one being played. */
  readonly seasonName?: string | undefined;
  readonly seasonIsActive?: boolean | undefined;
  /** Their role here, phrased for a person rather than for the database. */
  readonly role?: string | undefined;
  /** Unread announcements, when the screen already knows; else the shell asks. */
  readonly unreadAnnouncements?: number | undefined;
  readonly onSignOut: () => void;
  readonly children: React.ReactNode;
}

/**
 * A destination, carrying whichever team is currently selected.
 *
 * Plain `destination.to` when nothing is selected, so a person who has never
 * touched the switcher sees exactly the URLs this app always wrote. With a
 * selection, `?team=` rides along: `NavLink` does not carry an existing query
 * string across a navigation on its own, so tapping Schedule from a team a
 * person just switched to would otherwise land back on the server's default
 * team, silently undoing the choice they just made.
 */
export function linkTo(path: string, selectedTeamId: string | undefined): To {
  return selectedTeamId === undefined
    ? path
    : /*
       * Encoded. `searchParams.get` hands back a DECODED value, and writing it
       * straight into a template re-serialized it raw, so a value carrying an
       * ampersand truncated the selection on the next nav link. Not exploitable,
       * because the server validates against held memberships and team ids are
       * uuids, but silently losing the selection is the failure this screen
       * exists to prevent.
       */
      { pathname: path, search: `?${new URLSearchParams({ team: selectedTeamId }).toString()}` };
}

function Nav({
  className,
  selectedTeamId,
  destinations,
  unread,
}: {
  readonly className: string;
  readonly selectedTeamId: string | undefined;
  readonly destinations: readonly Destination[];
  readonly unread: number | null;
}) {
  return (
    <nav className={className} aria-label="Sections">
      <ul className="shell__navList">
        {destinations.map((destination) => (
          <li key={destination.id}>
            <NavLink
              to={linkTo(destination.to, selectedTeamId)}
              /*
               * Home is `/`, which is a prefix of every path, so it has to match
               * exactly or it would read as current on every screen.
               */
              end={destination.to === '/'}
              className={({ isActive }: { isActive: boolean }) =>
                isActive ? 'shell__navLink shell__navLink--current' : 'shell__navLink'
              }
            >
              <NavIcon id={destination.id} />
              <span className="shell__navLabel">{destination.label}</span>
              {/* The number as text, with a spoken form: colour is not the carrier. */}
              {destination.id === 'announcements' && unread !== null && unread > 0 ? (
                <>
                  <span className="shell__navBadge" aria-hidden="true">
                    {unread > 99 ? '99+' : unread}
                  </span>
                  <span className="sr-only">, {unread} unread</span>
                </>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Unread announcements for the badge: the screen's own count when it has
 * one (the announcements screen, which changes it as Got it is tapped), else
 * one read per shell mount and per selected team. Switching teams drops the
 * old count at once, so the badge never shows one team's number over
 * another's name while the new count is on its way; the client funnels
 * `?team=` onto the request. Silent on failure: a badge that cannot be read
 * is simply absent, and the screen behind it says why when it is opened.
 */
function useUnreadAnnouncements(
  enabled: boolean,
  known: number | undefined,
  teamId: string | undefined,
): number | null {
  const [fetched, setFetched] = useState<number | null>(null);
  useEffect(() => {
    setFetched(null);
    if (!enabled || known !== undefined) return;
    let cancelled = false;
    api<{ unread?: unknown }>('/api/communication/unread').then(
      (answer) => {
        if (!cancelled && typeof answer.unread === 'number') setFetched(answer.unread);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, known, teamId]);
  return known ?? fetched;
}

/**
 * The shell's own unread count, shared with the screens inside it so a Home
 * link can show the same number without a second request. Null outside a
 * shell; `available` is false for roles without the Announcements tab.
 */
interface AnnouncementsInShell {
  /** Null when not known: loading, failed, offline, or just after a team switch. */
  readonly unread: number | null;
  readonly available: boolean;
}

const AnnouncementsContext = createContext<AnnouncementsInShell | null>(null);

export function useAnnouncementsInShell(): AnnouncementsInShell | null {
  return useContext(AnnouncementsContext);
}

export function AppShell({
  organizationName,
  teamName,
  teamId,
  seasonName,
  seasonIsActive,
  role,
  unreadAnnouncements,
  onSignOut,
  children,
}: AppShellProps) {
  /*
   * Called here, once, rather than inside `TeamSwitcher`.
   *
   * `AppShell` is instantiated fresh by every screen that renders it
   * (`TeamScreen`, `ScheduleScreen` and `Landing` each render their own
   * `<AppShell>`), and a second `useTeams()` call inside the switcher would
   * mean two requests to `/api/team/memberships` on every one of those
   * mounts, for the same answer. One call here, passed down, is one request.
   */
  const { state: teamsState, selectedTeamId, selectTeam } = useTeams();
  const memberships = teamsState.kind === 'ready' ? teamsState.memberships : [];
  const club: ClubHome | undefined = teamsState.kind === 'ready' ? teamsState.club : undefined;
  const clubOnly = memberships.length === 0 && club !== undefined;
  const destinations = visibleDestinations(
    role,
    teamId ?? selectedTeamId,
    memberships,
    teamsState.kind === 'ready' && teamsState.canRestoreTeams,
    club !== undefined,
  );
  const canChangePassword =
    club !== undefined ||
    canHoldPassword(
      memberships.flatMap((membership) => {
        const held = RoleSchema.safeParse(membership.role);
        return held.success ? [held.data] : [];
      }),
    );
  const unread = useUnreadAnnouncements(
    destinations.some((destination) => destination.id === 'announcements'),
    unreadAnnouncements,
    teamId ?? selectedTeamId,
  );

  const announcements: AnnouncementsInShell = {
    unread,
    available: destinations.some((destination) => destination.id === 'announcements'),
  };

  const name = teamName ?? club?.organizationName ?? 'Good Season';
  const fittedName = useHeaderFit(name);

  const activeTeamId =
    teamId ??
    selectedTeamId ??
    (teamsState.kind === 'ready' ? teamsState.currentTeamId : undefined);
  const activeOrganizationName =
    organizationName ??
    (teamsState.kind === 'ready'
      ? memberships.find((membership) => membership.teamId === activeTeamId)?.organizationName
      : undefined);

  // Season, then club, on one quiet line under the team name.
  const meta = [
    seasonName === undefined
      ? null
      : `${seasonName}${seasonIsActive === true ? ' \u00b7 Active' : ''}`,
    activeOrganizationName ?? null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__branding">
          <BrandMark className="shell__mark" />

          <div className="shell__identity">
            {/* Decorative: the team name beside it is the name. */}
            {teamName === undefined ? null : (
              <span className="shell__badge" aria-hidden="true">
                {initialsOf(teamName)}
              </span>
            )}
            <div className="shell__identityText">
              {/*
              The page's ONE h1, and it belongs here rather than on each screen.
              `/team`'s ready state had no h1 at all after the header moved into
              the shell: its highest heading was the roster's h2, so the screen's
              title was a paragraph. `page-has-heading-one` carries axe's
              `best-practice` tag and the a11y spec enables that tag, so it is a
              real violation and not a preference. Screens now nest their own
              headings under this one.
            */}
              <h1
                className="shell__team"
                ref={fittedName.ref}
                style={{ fontSize: fittedName.px }}
                title={name}
                dir="auto"
              >
                {name}
              </h1>
              {meta.length === 0 ? null : (
                <p
                  className="shell__meta shell__singleLine"
                  title={meta.join(' \u00b7 ')}
                  dir="auto"
                >
                  {meta.join(' \u00b7 ')}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="shell__account">
          {role === undefined && !clubOnly ? null : (
            <p className="shell__role">
              Signed in as{' '}
              {role !== undefined
                ? role.replace(/_/g, ' ')
                : club?.scopeKind === 'division_head'
                  ? 'division head'
                  : 'club owner'}
            </p>
          )}
          {/*
            Renders nothing below two memberships, still loading, or failed:
            see TeamSwitcher's own header for why each of those is the
            designed answer rather than a gap. Either way this shell keeps
            rendering everything else regardless of what `useTeams()` did,
            which is the whole point: a person who cannot list their teams can
            still use the one the server already gave them.
          */}
          <TeamSwitcher
            memberships={memberships}
            currentTeamId={teamId ?? selectedTeamId}
            onSelect={selectTeam}
          />
          {/*
           * A real button, not a link. Signing out is an action with a side
           * effect on the server, and `CLAUDE.md` wants 44px of touch target
           * even when the label is two words.
           */}
          {/* Every role: the change log is for coaches and parents alike. */}
          <Link to={linkTo('/changelog', selectedTeamId)} className="shell__signOut">
            What's new
          </Link>
          {/*
            Staff only (ADR-0101): a guardian has no password, and a link to a
            screen that can only refuse them is a dead end.
          */}
          {canChangePassword ? (
            <Link to={linkTo('/account/password', selectedTeamId)} className="shell__signOut">
              Change password
            </Link>
          ) : null}
          <Link to={linkTo('/account/name', teamId ?? selectedTeamId)} className="shell__signOut">
            Change your name
          </Link>
          <button type="button" className="shell__signOut" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      {/* Wide screens only. On a phone this is the bottom bar instead. */}
      <Nav
        className="shell__nav shell__nav--top"
        selectedTeamId={selectedTeamId}
        destinations={destinations}
        unread={unread}
      />

      <main className="shell__main">
        <AnnouncementsContext.Provider value={announcements}>
          {children}
        </AnnouncementsContext.Provider>
      </main>

      <footer className="shell__footer">
        <p className="shell__footerLine">Good Season</p>
        {/*
          Was "Built for one team at a time." That stopped being true the day
          the switcher above shipped: the server has resolved more than one
          team per person since the first migration, and this line was the
          part of the app actively telling somebody otherwise.
        */}
        <p className="shell__footerLine shell__footerLine--faint">
          {clubOnly && club !== undefined
            ? club.organizationName
            : "Every team you're on, in one place."}
        </p>
      </footer>

      {/* Phones only. Last in the DOM so it does not precede the content a
          screen reader reaches first, and fixed to the bottom in CSS. */}
      <Nav
        className="shell__nav shell__nav--bottom"
        selectedTeamId={selectedTeamId}
        destinations={destinations}
        unread={unread}
      />
    </div>
  );
}
